import { useState } from 'react';
import { Button, Card, CardContent, FilterSelect, ServerSearchInput, TenantSelector } from '@/components/ui';
import {
  TEMPLATE_CHANNEL_FILTER_OPTIONS,
  TEMPLATE_TECH_TYPE_OPTIONS,
  TEMPLATE_THEME_OPTIONS,
  type TemplateUsageFilter,
} from '@/features/templates/templateTaxonomy';

interface TemplatesFilterBarProps {
  isSuperAdmin: boolean;
  selectedTenantId: string;
  onTenantChange: (tenantId: string) => void;
  searchQuery: string;
  onSearchQueryChange: (value: string) => void;
  typeFilter: string;
  onTypeFilterChange: (value: string) => void;
  messengerFilter: string;
  onMessengerFilterChange: (value: string) => void;
  categoryFilter: string;
  onCategoryFilterChange: (value: string) => void;
  activeFilter: 'all' | 'active' | 'inactive';
  onActiveFilterChange: (value: 'all' | 'active' | 'inactive') => void;
  usageFilter: TemplateUsageFilter;
  onUsageFilterChange: (value: TemplateUsageFilter) => void;
  templatesCount: number;
  activeTemplatesCount: number;
  inactiveTemplatesCount: number;
  templateUsageCounts: {
    manual: number;
    eventDriven: number;
    scheduled: number;
    cluster: number;
    postProcedure: number;
    replyChain: number;
  };
  /** 08.10.26 — клиника живёт в панели разделов, здесь её можно скрыть. Не удалять. */
  showTenantSelector?: boolean;
}

export function TemplatesFilterBar({
  isSuperAdmin,
  selectedTenantId,
  onTenantChange,
  searchQuery,
  onSearchQueryChange,
  typeFilter,
  onTypeFilterChange,
  messengerFilter,
  onMessengerFilterChange,
  categoryFilter,
  onCategoryFilterChange,
  activeFilter,
  onActiveFilterChange,
  usageFilter,
  onUsageFilterChange,
  templatesCount,
  activeTemplatesCount,
  inactiveTemplatesCount,
  templateUsageCounts,
  showTenantSelector = true,
}: TemplatesFilterBarProps) {
  const [extraOpen, setExtraOpen] = useState(false);
  const showExtra = extraOpen || Boolean(typeFilter || categoryFilter);

  return (
    <Card className="border-shell-border bg-shell-surface shadow-none">
      <CardContent className="space-y-4 pt-5">
        <div className="grid grid-cols-1 gap-3 xl:grid-cols-12">
          {showTenantSelector && isSuperAdmin && (
            <div className="xl:col-span-2">
              <TenantSelector
                value={selectedTenantId}
                onChange={onTenantChange}
                testId="select-tenant-filter"
                showLabel={false}
              />
            </div>
          )}
          <div className={showTenantSelector && isSuperAdmin ? 'xl:col-span-4' : 'xl:col-span-6'}>
            <ServerSearchInput
              value={searchQuery}
              onChange={onSearchQueryChange}
              placeholder="Поиск по названию или содержимому..."
              hint="Серверный поиск по всем шаблонам клиники, не только по текущим строкам."
              testId="input-search-templates"
            />
          </div>
          <div className="xl:col-span-3">
            <FilterSelect
              value={messengerFilter}
              onChange={onMessengerFilterChange}
              options={TEMPLATE_CHANNEL_FILTER_OPTIONS}
              placeholder="Все каналы"
              allOptionLabel="Все каналы"
              testId="select-messenger-filter"
            />
          </div>
          <div className="flex items-center xl:col-span-3">
            <button type="button" className="shell-button-secondary h-9" onClick={() => setExtraOpen((open) => !open)}>
              Все фильтры
            </button>
          </div>
        </div>
        <div className={showExtra ? 'grid grid-cols-1 gap-3 md:grid-cols-2' : 'hidden'}>
          <FilterSelect
            value={typeFilter}
            onChange={onTypeFilterChange}
            options={TEMPLATE_TECH_TYPE_OPTIONS.map((option) => ({
              value: option.value,
              label: option.label,
            }))}
            placeholder="Все тех. типы"
            allOptionLabel="Все тех. типы"
            testId="select-type-filter"
          />
          <FilterSelect
            value={categoryFilter}
            onChange={onCategoryFilterChange}
            options={TEMPLATE_THEME_OPTIONS.map((category) => ({
              value: category.value,
              label: category.label,
            }))}
            placeholder="Все тематики"
            allOptionLabel="Все тематики"
            testId="select-category-filter"
          />
        </div>

        <div className="grid gap-3 lg:grid-cols-2">
          <div className="space-y-2">
            <div className="text-xs font-semibold uppercase tracking-[0.14em] text-shell-muted">
              Статус
            </div>
            <div className="inline-flex flex-wrap gap-1 rounded-xl border border-shell-border bg-shell-elevated/50 p-1">
              <Button
                variant={activeFilter === 'all' ? 'primary' : 'ghost'}
                onClick={() => onActiveFilterChange('all')}
                size="sm"
                testId="btn-filter-all"
                className="h-8"
              >
                Все ({templatesCount})
              </Button>
              <Button
                variant={activeFilter === 'active' ? 'primary' : 'ghost'}
                onClick={() => onActiveFilterChange('active')}
                size="sm"
                testId="btn-filter-active"
                className="h-8"
              >
                Активные ({activeTemplatesCount})
              </Button>
              <Button
                variant={activeFilter === 'inactive' ? 'primary' : 'ghost'}
                onClick={() => onActiveFilterChange('inactive')}
                size="sm"
                testId="btn-filter-inactive"
                className="h-8"
              >
                Неактивные ({inactiveTemplatesCount})
              </Button>
            </div>
          </div>

          <div className="space-y-2">
            <div className="text-xs font-semibold uppercase tracking-[0.14em] text-shell-muted">
              Сценарий использования
            </div>
            <div className="flex flex-wrap gap-1.5">
              <Button
                variant={usageFilter === '' ? 'primary' : 'secondary'}
                onClick={() => onUsageFilterChange('')}
                size="sm"
                className="h-8"
              >
                Все сценарии
              </Button>
              <Button
                variant={usageFilter === 'manual' ? 'primary' : 'secondary'}
                onClick={() => onUsageFilterChange('manual')}
                size="sm"
                testId="btn-filter-manual"
                className="h-8"
              >
                Ручные ({templateUsageCounts.manual})
              </Button>
              <Button
                variant={usageFilter === 'event_driven' ? 'primary' : 'secondary'}
                onClick={() => onUsageFilterChange('event_driven')}
                size="sm"
                className="h-8"
              >
                Event-Driven ({templateUsageCounts.eventDriven})
              </Button>
              <Button
                variant={usageFilter === 'scheduled' ? 'primary' : 'secondary'}
                onClick={() => onUsageFilterChange('scheduled')}
                size="sm"
                className="h-8"
              >
                По расписанию ({templateUsageCounts.scheduled})
              </Button>
              <Button
                variant={usageFilter === 'cluster' ? 'primary' : 'secondary'}
                onClick={() => onUsageFilterChange('cluster')}
                size="sm"
                className="h-8"
              >
                Кластерные ({templateUsageCounts.cluster})
              </Button>
              <Button
                variant={usageFilter === 'post_procedure' ? 'primary' : 'secondary'}
                onClick={() => onUsageFilterChange('post_procedure')}
                size="sm"
                className="h-8"
              >
                После процедур ({templateUsageCounts.postProcedure})
              </Button>
              <Button
                variant={usageFilter === 'reply_chain' ? 'primary' : 'secondary'}
                onClick={() => onUsageFilterChange('reply_chain')}
                size="sm"
                className="h-8"
              >
                Auto-Reply ({templateUsageCounts.replyChain})
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
